// In-app help content for the SMS Gateway portal.
//
// Each section maps to a tab in the help drawer; each topic is a pill with an
// HTML body (100–300 words). Icons are FontAwesome CSS classes (the app renders
// them via <i className={icon} />), matching the rest of the UI. Route → section
// mapping (getDefaultSection/getDefaultTopic) opens the drawer on the most
// relevant topic for whatever page the user is on.

export const helpSections = [
  {
    id: 'welcome',
    title: 'Welcome',
    icon: 'fa fa-rocket',
    topics: [
      {
        id: 'overview',
        title: 'Overview',
        body: `
          <p>The <strong>SMS Gateway</strong> is the DICT enterprise messaging platform for government agencies and LGUs. It sends <strong>SMS</strong>, <strong>Email</strong>, and <strong>Push</strong> notifications from one place, with approved Sender ID masks, credit tracking, and real-time delivery analytics.</p>
          <p style="margin-top:12px;"><strong>What you can do here:</strong></p>
          <ul>
            <li><strong>Send messages</strong> — to individuals, contact groups, or in bulk via CSV.</li>
            <li><strong>Manage contacts &amp; Sender IDs</strong> — organize recipients and send under your official brand.</li>
            <li><strong>Track credits</strong> — monitor your balance, allocation, and subscription validity.</li>
            <li><strong>Review &amp; report</strong> — dashboards, statistical reports, and full audit logs.</li>
          </ul>
          <p style="margin-top:8px; font-size:13px; color:#9ca3af;">Tip: the help button (?) is available on every page and opens to the topic most relevant to where you are.</p>
        `,
      },
      {
        id: 'roles',
        title: 'Roles & Access',
        body: `
          <p>What you see is controlled by your <strong>role</strong>. The portal has four roles, each scoped to the tasks they need:</p>
          <ul>
            <li><span style="color:#667eea;"><strong>Platform Administrator</strong></span> — full access: users, credits, reports, incidents, training, and all messaging.</li>
            <li><span style="color:#22c55e;"><strong>Messaging User</strong></span> — send SMS/Email/Push, manage contacts, templates, and request Sender IDs.</li>
            <li><span style="color:#3b82f6;"><strong>Compliance / Audit Officer</strong></span> — read access to audit logs and message history.</li>
            <li><span style="color:#f59e0b;"><strong>Integration / System Administrator</strong></span> — API keys and webhook configuration.</li>
          </ul>
          <p style="margin-top:8px;">Menu items and pages you are not authorized for are hidden automatically. If you need access to something you can't see, contact your Platform Administrator.</p>
        `,
      },
      {
        id: 'signing-in',
        title: 'Signing In (MFA)',
        body: `
          <p>The portal uses <strong>multi-factor authentication</strong> for every session. Signing in is a two-step flow:</p>
          <ol>
            <li><strong>Credentials</strong> — enter your email and password, then click <strong>Sign In</strong>.</li>
            <li style="margin-top:8px;"><strong>SMS OTP</strong> — a 6-digit code is sent to your registered mobile number. Enter it on the verification screen to complete sign-in.</li>
          </ol>
          <p style="margin-top:8px;">Codes expire after 5 minutes. Use <strong>Resend Code</strong> if it lapses. After 5 failed password attempts your account locks for 15 minutes.</p>
          <div style="background:#f8f7ff; border:1px solid #e5e7eb; border-radius:8px; padding:12px; margin-top:16px;">
            <p style="color:#667eea; font-weight:600; margin-bottom:8px;">Demo / UAT environments</p>
            <p style="font-size:13px;">On demo deployments the OTP is shown on-screen and one-click role logins are available, so you can explore without a live SMS gateway.</p>
          </div>
        `,
      },
    ],
  },
  {
    id: 'messaging',
    title: 'Messaging',
    icon: 'fa fa-paper-plane',
    roles: ['user', 'admin'],
    topics: [
      {
        id: 'compose-sms',
        title: 'Compose SMS',
        body: `
          <p>Use <strong>Messaging → Compose SMS</strong> to send a message to one or more recipients.</p>
          <ol>
            <li><strong>Sender ID</strong> — pick an approved mask the message is sent under.</li>
            <li style="margin-top:8px;"><strong>Recipient</strong> — enter a mobile number (PH <strong>+639XXXXXXXXX</strong> or international E.164).</li>
            <li style="margin-top:8px;"><strong>Message</strong> — type the body; the segment count and credit cost update as you type.</li>
            <li style="margin-top:8px;">Click <strong>Send</strong>. The message appears in your history with a live status.</li>
          </ol>
          <p style="margin-top:8px;">Each SMS segment costs 1 credit and is deducted from your balance on send. Messages are classified <strong>Domestic</strong> or <strong>International</strong> automatically for reporting.</p>
        `,
      },
      {
        id: 'group-sms',
        title: 'Group SMS',
        body: `
          <p><strong>Messaging → Group SMS</strong> broadcasts one message to every contact in a selected group (e.g. <em>LGU Coordinators</em>, <em>Health Workers</em>).</p>
          <ol>
            <li><strong>Choose audience</strong> — select one or more contact groups; the recipient count is shown before you send.</li>
            <li style="margin-top:8px;"><strong>Compose</strong> — pick a Sender ID and write the message (or load a template).</li>
            <li style="margin-top:8px;"><strong>Review &amp; send</strong> — confirm the recipient count and credit cost, then broadcast.</li>
          </ol>
          <p style="margin-top:8px;">Completed broadcasts are recorded as campaigns with sent counts and delivery results, viewable in your message history.</p>
        `,
      },
      {
        id: 'bulk-upload',
        title: 'Bulk Upload (CSV)',
        body: `
          <p><strong>Messaging → Bulk Upload</strong> sends to a large recipient list from a CSV file.</p>
          <ol>
            <li><strong>Upload CSV</strong> — provide a file of recipient numbers. Invalid rows are flagged and skipped.</li>
            <li style="margin-top:8px;"><strong>Validate</strong> — the system reports valid, invalid, and duplicate counts before sending.</li>
            <li style="margin-top:8px;"><strong>Compose &amp; send</strong> — choose a Sender ID, write the message, and dispatch the batch.</li>
          </ol>
          <p style="margin-top:8px; font-size:13px; color:#9ca3af;">Tip: clean your list first — every valid recipient consumes credits. The validation step shows the exact count and cost before anything is sent.</p>
        `,
      },
      {
        id: 'templates',
        title: 'Message Templates',
        body: `
          <p><strong>Messaging → Templates</strong> stores reusable message bodies so you don't retype common notices.</p>
          <p style="margin-top:8px;">Templates support <strong>{{placeholders}}</strong> — e.g. <em>"Hi {{name}}, your appointment is on {{date}}."</em> Fill the values in when composing.</p>
          <ul>
            <li><strong>Create</strong> — give the template a name, category (Notification, Reminder, Alert), Sender ID, and body.</li>
            <li><strong>Use</strong> — pick a template on the compose screen to pre-fill the message.</li>
            <li><strong>Edit / delete</strong> — keep your library current; changes apply to future sends only.</li>
          </ul>
        `,
      },
      {
        id: 'delivery-status',
        title: 'Delivery Status',
        body: `
          <p>Every message carries a live status so you can confirm receipt:</p>
          <p style="font-size:13px; margin-top:8px;">
            <span style="color:#6b7280;">Pending</span> &rarr;
            <span style="color:#3b82f6;">Sent</span> &rarr;
            <span style="color:#22c55e;">Delivered</span>
            &nbsp;|&nbsp; <span style="color:#ef4444;">Failed</span>
          </p>
          <ul style="margin-top:8px;">
            <li><strong>Pending</strong> — queued, not yet handed to the carrier.</li>
            <li><strong>Sent</strong> — accepted by the gateway, awaiting a delivery receipt.</li>
            <li><strong>Delivered</strong> — confirmed received on the handset.</li>
            <li><strong>Failed</strong> — rejected or undeliverable (invalid number, carrier issue).</li>
          </ul>
          <p style="margin-top:8px;">Your dashboard summarizes these as a delivery-rate donut and a daily volume chart. Full per-message detail lives under <strong>Audit Logs → Message History</strong>.</p>
        `,
      },
    ],
  },
  {
    id: 'channels',
    title: 'Email & Push',
    icon: 'fa fa-bell',
    roles: ['user', 'admin'],
    topics: [
      {
        id: 'email',
        title: 'Email Notifications',
        body: `
          <p><strong>Messaging → Compose Email</strong> sends transactional or bulk email alongside SMS, from the same platform.</p>
          <ol>
            <li><strong>Compose</strong> — enter the recipient, subject, and body, or load an email template.</li>
            <li style="margin-top:8px;"><strong>Schedule (optional)</strong> — set a future send time, or leave blank to send immediately.</li>
            <li style="margin-top:8px;"><strong>Preview</strong> — render the email with placeholders filled before sending.</li>
          </ol>
          <p style="margin-top:8px;">Email analytics track <strong>sent, delivered, opened, bounced,</strong> and <strong>failed</strong>. Failed sends are retried automatically. Manage reusable bodies under <strong>Email Templates</strong>.</p>
          <p style="margin-top:8px; font-size:13px; color:#9ca3af;">Domain authentication (SPF / DKIM / DMARC) is configured at the platform level for deliverability.</p>
        `,
      },
      {
        id: 'push',
        title: 'Push Notifications',
        body: `
          <p><strong>Messaging → Push Notifications</strong> delivers app notifications to registered devices across iOS, Android, and Web.</p>
          <p style="margin-top:8px;"><strong>Register a device:</strong></p>
          <ol>
            <li>Choose the <strong>platform</strong> (iOS / Android / Web) and paste the device token.</li>
            <li style="margin-top:8px;">Save it — registered devices appear in the list and can be removed anytime.</li>
          </ol>
          <p style="margin-top:12px;"><strong>Send a notification:</strong></p>
          <ul>
            <li><strong>Target</strong> — a specific token, a topic, a group, or an individual.</li>
            <li><strong>Priority</strong> — <span style="color:#ef4444;">high</span> or <span style="color:#6b7280;">normal</span>.</li>
            <li><strong>Payload</strong> — title, body, and optional custom data.</li>
          </ul>
          <p style="margin-top:8px;">Delivery analytics (sent / delivered / failed) appear at the top of the page.</p>
        `,
      },
    ],
  },
  {
    id: 'contacts',
    title: 'Contacts & Sender IDs',
    icon: 'fa fa-address-book',
    roles: ['user', 'admin'],
    topics: [
      {
        id: 'contacts',
        title: 'Contacts & Groups',
        body: `
          <p><strong>Contacts</strong> is your recipient directory. Organize people into <strong>groups</strong> so you can broadcast to an audience in one step.</p>
          <ul>
            <li><strong>Add a contact</strong> — name, mobile number, email, and organization.</li>
            <li><strong>Group</strong> — assign contacts to groups (e.g. <em>Emergency Response Team</em>, <em>Field Agents</em>).</li>
            <li><strong>Import</strong> — bulk-add contacts from CSV.</li>
            <li><strong>Bulk actions</strong> — move to a group or delete multiple at once.</li>
          </ul>
          <p style="margin-top:8px;">Numbers are validated on entry (PH <strong>+639XXXXXXXXX</strong> or international E.164) so broadcasts don't waste credits on malformed entries.</p>
        `,
      },
      {
        id: 'sender-ids',
        title: 'Sender IDs (Masks)',
        body: `
          <p>A <strong>Sender ID</strong> (mask) is the name recipients see as the sender — e.g. <strong>DICT</strong> or <strong>DOH</strong> — instead of a raw number. Messages must be sent under an <span style="color:#22c55e;">Approved</span> mask.</p>
          <p style="margin-top:8px;"><strong>Request a mask:</strong> submit the mask name, your organization, and supporting authorization. It enters the queue as <span style="color:#f59e0b;">Pending Approval</span>.</p>
          <p style="margin-top:8px;"><strong>Statuses:</strong> <span style="color:#f59e0b;">Pending Approval</span> &rarr; <span style="color:#22c55e;">Approved</span> or <span style="color:#ef4444;">Rejected</span> (with a reason).</p>
          <p style="margin-top:8px;">Administrators approve or reject requests from the same queue. The platform allows up to <strong>150 active approved masks</strong>.</p>
        `,
      },
    ],
  },
  {
    id: 'credits',
    title: 'Credits',
    icon: 'fa fa-wallet',
    topics: [
      {
        id: 'my-credits',
        title: 'My Balance & Subscription',
        body: `
          <p><strong>My Credits</strong> shows your available SMS credit balance, a low-balance threshold alert, and your subscription validity.</p>
          <ul>
            <li><strong>Balance</strong> — your current credits; also shown live in the top bar.</li>
            <li><strong>Threshold</strong> — set a low-balance alert level so you're warned before running out.</li>
            <li><strong>Ledger</strong> — every allocation and deduction, with the running balance.</li>
            <li><strong>Subscription</strong> — the 6-month validity window and days remaining.</li>
          </ul>
          <p style="margin-top:8px;">Each SMS segment deducts 1 credit on send. When your balance hits the threshold, an alert is raised to you and your administrator.</p>
        `,
      },
      {
        id: 'allocation',
        title: 'Credit Allocation (Admin)',
        roles: ['admin'],
        body: `
          <p>Administrators use <strong>Credits</strong> to distribute the master credit pool across organizations, groups, and users.</p>
          <ul>
            <li><strong>Allocate</strong> — move credits from the master pool to a recipient account.</li>
            <li><strong>Reallocate</strong> — return unused credits from an account back to the pool.</li>
            <li><strong>Ledger</strong> — an immutable record of every allocation and reallocation.</li>
          </ul>
          <p style="margin-top:8px;">The platform is provisioned with a fixed contract pool. When cumulative allocation crosses <strong>60%</strong> of the contract, a threshold alert is raised so usage can be reported to DICT.</p>
        `,
      },
    ],
  },
  {
    id: 'admin',
    title: 'Administration',
    icon: 'fa fa-user-shield',
    roles: ['admin', 'compliance'],
    topics: [
      {
        id: 'users',
        title: 'User Administration',
        roles: ['admin'],
        body: `
          <p><strong>Administration → User Admin</strong> (Platform Administrators only) manages portal accounts.</p>
          <ul>
            <li><strong>Create</strong> — add a user with name, email, mobile, organization, and role.</li>
            <li><strong>Update</strong> — change details or reassign a role.</li>
            <li><strong>Deactivate</strong> — set a user <span style="color:#ef4444;">Inactive</span> to block sign-in without deleting history.</li>
          </ul>
          <p style="margin-top:8px;">A user's role determines exactly which pages and actions they can reach (see <em>Welcome → Roles &amp; Access</em>).</p>
        `,
      },
      {
        id: 'reports',
        title: 'Reports & Dashboards',
        roles: ['admin', 'compliance'],
        body: `
          <p>The <strong>System Dashboard</strong> gives org-wide visibility: active users, credit pool status, delivery breakdown, and volume over time.</p>
          <p style="margin-top:8px;"><strong>Reports</strong> generates statistics for a chosen period:</p>
          <ul>
            <li><strong>Daily / Monthly / Yearly</strong> messaging statistics.</li>
            <li><strong>Credit utilization per Sender ID mask</strong>, including a <strong>Domestic vs International</strong> split.</li>
          </ul>
          <p style="margin-top:8px;">Use these for the monthly utilization reporting required under the service agreement.</p>
        `,
      },
      {
        id: 'audit',
        title: 'Audit Logs',
        roles: ['admin', 'compliance'],
        body: `
          <p><strong>Audit Logs</strong> provides the compliance trail (Administrators and Compliance Officers).</p>
          <ul>
            <li><strong>Message History</strong> — every message with recipient, Sender ID, status, and timestamp.</li>
            <li><strong>System Activity</strong> — logins, MFA challenges, failed attempts, lockouts, and config changes.</li>
          </ul>
          <p style="margin-top:8px;">Logs are searchable and filterable for audits and investigations. They are append-only — entries are never edited or removed.</p>
        `,
      },
      {
        id: 'incidents-training',
        title: 'Incidents & Training',
        roles: ['admin'],
        body: `
          <p><strong>Support → SLA Support Incidents</strong> tracks service incidents against the response matrix.</p>
          <ul>
            <li>Log an incident with severity <span style="color:#ef4444;">Critical</span> / <span style="color:#f59e0b;">Major</span> / <span style="color:#3b82f6;">Minor</span>, description, and affected organizations.</li>
            <li>Each severity has an SLA resolution target; breaches and any credit penalties are tracked.</li>
          </ul>
          <p style="margin-top:12px;"><strong>Support → Project Training</strong> manages training sessions:</p>
          <ul>
            <li>Schedule End-User, Administrator, or Technical tracks.</li>
            <li>Enroll participants and record pre/post assessment scores; a post score ≥ 80% marks them <span style="color:#22c55e;">Certified</span>.</li>
          </ul>
        `,
      },
      {
        id: 'portal-export',
        title: 'Portal Content & Data Export',
        roles: ['admin'],
        body: `
          <p><strong>Administration → Portal Content</strong> lets administrators edit text shown on the portal (e.g. the login headline, announcements, support email) without a code change.</p>
          <p style="margin-top:12px;"><strong>Administration → Data Export</strong> supports data turnover:</p>
          <ul>
            <li><strong>Full export</strong> — download a JSON bundle of all core data.</li>
            <li><strong>Per-entity CSV</strong> — export a single table (users, messages, contacts, etc.).</li>
          </ul>
          <p style="margin-top:8px; font-size:13px; color:#9ca3af;">Sensitive fields (password hashes, secrets) are stripped from every export.</p>
        `,
      },
    ],
  },
  {
    id: 'integrations',
    title: 'Integrations',
    icon: 'fa fa-code',
    roles: ['admin', 'integration'],
    topics: [
      {
        id: 'api-keys',
        title: 'API Keys',
        body: `
          <p><strong>Integrations → API Settings</strong> (Integration Administrators) issues credentials for server-to-server messaging through the gateway API.</p>
          <ol>
            <li><strong>Generate</strong> a key with a name, optional IP whitelist (CIDR), and optional expiry.</li>
            <li style="margin-top:8px;">Copy the <strong>secret</strong> — it is shown exactly once and stored only as a hash.</li>
            <li style="margin-top:8px;">Use the key to call the REST gateway; requests are logged for usage and auditing.</li>
          </ol>
          <p style="margin-top:8px;">Keys can be <span style="color:#22c55e;">Active</span>, rotated, or <span style="color:#ef4444;">Revoked</span>. Revoking a key immediately blocks any integration using it.</p>
        `,
      },
      {
        id: 'webhooks',
        title: 'Webhooks',
        body: `
          <p><strong>Integrations → Webhooks</strong> registers callback URLs that receive delivery events in real time.</p>
          <ul>
            <li><strong>URL</strong> — your HTTPS endpoint that receives event payloads.</li>
            <li><strong>Events</strong> — subscribe to <em>message.delivered</em>, <em>message.failed</em>, <em>message.inbound</em>, and more.</li>
            <li><strong>Secret</strong> — a shared HMAC secret to verify each payload's authenticity.</li>
          </ul>
          <p style="margin-top:8px;">Every dispatch attempt is recorded with the response code, latency, and attempt number. Failed deliveries are retried on a schedule. Webhooks can be paused without deleting them.</p>
        `,
      },
    ],
  },
  {
    id: 'guide',
    title: 'How To',
    icon: 'fa fa-book-open',
    topics: [
      {
        id: 'first-sms',
        title: 'Send your first SMS',
        roles: ['user', 'admin'],
        body: `
          <ol>
            <li><strong>Confirm a Sender ID</strong> — go to <strong>Sender ID</strong> and make sure you have an <span style="color:#22c55e;">Approved</span> mask. If not, request one.</li>
            <li style="margin-top:8px;"><strong>Add a recipient</strong> — under <strong>Contacts</strong>, add a contact (or just have a number ready).</li>
            <li style="margin-top:8px;"><strong>Compose</strong> — open <strong>Messaging → Compose SMS</strong>, pick the Sender ID, enter the number, and type the message.</li>
            <li style="margin-top:8px;"><strong>Send</strong> — click <strong>Send</strong> and watch the status move from <span style="color:#3b82f6;">Sent</span> to <span style="color:#22c55e;">Delivered</span>.</li>
          </ol>
          <p style="margin-top:8px; font-size:13px; color:#9ca3af;">Tip: check your balance in the top bar first — sending deducts credits.</p>
        `,
      },
      {
        id: 'run-campaign',
        title: 'Run a bulk campaign',
        roles: ['user', 'admin'],
        body: `
          <ol>
            <li><strong>Prepare recipients</strong> — group contacts under <strong>Contacts</strong>, or prepare a clean CSV.</li>
            <li style="margin-top:8px;"><strong>Choose the channel</strong> — <strong>Group SMS</strong> for a saved group, or <strong>Bulk Upload</strong> for a CSV.</li>
            <li style="margin-top:8px;"><strong>Validate</strong> — review the valid / invalid / duplicate counts and the total credit cost.</li>
            <li style="margin-top:8px;"><strong>Compose &amp; send</strong> — pick a Sender ID, load a template if you have one, and broadcast.</li>
            <li style="margin-top:8px;"><strong>Review</strong> — track results on your dashboard and in <strong>Message History</strong>.</li>
          </ol>
        `,
      },
      {
        id: 'request-mask',
        title: 'Request a Sender ID',
        roles: ['user', 'admin'],
        body: `
          <ol>
            <li>Open <strong>Sender ID</strong> and choose to request a new mask.</li>
            <li style="margin-top:8px;">Enter the <strong>mask name</strong> (the brand recipients will see), your <strong>organization</strong>, and attach <strong>supporting authorization</strong>.</li>
            <li style="margin-top:8px;">Submit — the request enters the queue as <span style="color:#f59e0b;">Pending Approval</span>.</li>
            <li style="margin-top:8px;">An administrator reviews it and either <span style="color:#22c55e;">Approves</span> or <span style="color:#ef4444;">Rejects</span> it (with a reason).</li>
          </ol>
          <p style="margin-top:8px;">Once approved, the mask becomes selectable on every compose screen.</p>
        `,
      },
      {
        id: 'export-data',
        title: 'Export data for turnover',
        roles: ['admin'],
        body: `
          <ol>
            <li>Open <strong>Administration → Data Export</strong> (Administrators only).</li>
            <li style="margin-top:8px;">For a complete snapshot, download the <strong>full JSON bundle</strong>.</li>
            <li style="margin-top:8px;">For a single dataset, choose an <strong>entity</strong> (users, messages, contacts, …) and export it as <strong>CSV</strong>.</li>
          </ol>
          <p style="margin-top:8px;">Exports strip sensitive fields automatically, so they're safe to hand over.</p>
        `,
      },
    ],
  },
  {
    id: 'faq',
    title: 'FAQ',
    icon: 'fa fa-circle-question',
    topics: [
      {
        id: 'no-otp',
        title: "I didn't get my OTP",
        body: `
          <p>The OTP is sent to your <strong>registered mobile number</strong> and is valid for 5 minutes.</p>
          <ul>
            <li>Wait a few seconds, then use <strong>Resend Code</strong>.</li>
            <li>Confirm your mobile number is correct under <strong>My Profile</strong>.</li>
            <li>If your account is locked after repeated attempts, wait 15 minutes and try again.</li>
          </ul>
          <p style="margin-top:8px;">On demo/UAT environments the code is shown on the verification screen.</p>
        `,
      },
      {
        id: 'why-failed',
        title: 'Why did a message fail?',
        body: `
          <p>A <span style="color:#ef4444;">Failed</span> status usually means one of:</p>
          <ul>
            <li><strong>Invalid number</strong> — malformed or unreachable (not a valid mobile).</li>
            <li><strong>Carrier rejection</strong> — the network refused the message.</li>
            <li><strong>Sender ID</strong> — the mask wasn't approved for that destination.</li>
          </ul>
          <p style="margin-top:8px;">Open <strong>Audit Logs → Message History</strong> to see the per-message detail, then correct the number or Sender ID and resend.</p>
        `,
      },
      {
        id: 'credits-run-out',
        title: 'What happens when credits run low?',
        body: `
          <p>Each SMS segment deducts 1 credit. When your balance reaches the <strong>low-balance threshold</strong> you set in <strong>My Credits</strong>, an alert is raised to you and your administrator.</p>
          <p style="margin-top:8px;">If you run out, sends are blocked until an administrator <strong>allocates</strong> more credits to your account. Your subscription also has a 6-month validity window shown on the same page.</p>
        `,
      },
      {
        id: 'cant-see',
        title: "Why can't I see a menu item?",
        body: `
          <p>The portal hides pages your <strong>role</strong> isn't authorized for. For example, only Platform Administrators see <strong>User Admin</strong> and <strong>Credit Allocation</strong>; only Integration Administrators see <strong>API Settings</strong>.</p>
          <p style="margin-top:8px;">If you need access to a hidden area, ask your Platform Administrator to grant the appropriate role. See <em>Welcome → Roles &amp; Access</em> for the full breakdown.</p>
        `,
      },
      {
        id: 'channels-diff',
        title: 'SMS vs Email vs Push — which do I use?',
        body: `
          <ul>
            <li><strong>SMS</strong> — urgent, high-reach alerts to any mobile number; no app or internet needed on the recipient side.</li>
            <li><strong>Email</strong> — longer, formatted content; transactional receipts and bulk notices; supports scheduling and templates.</li>
            <li><strong>Push</strong> — instant notifications to users who have your app installed and a registered device token.</li>
          </ul>
          <p style="margin-top:8px;">All three are sent and tracked from the same portal, so you can choose the right channel per message.</p>
        `,
      },
    ],
  },
];

// ── RBAC ────────────────────────────────────────────────────────────────────
// A section/topic with no `roles` is visible to everyone (mirrors rbac.js, where
// an undefined route role means "any authenticated user"). Otherwise the user's
// role must be listed. Role codes match config/rbac.js: admin | user |
// compliance | integration.
function roleAllowed(role, roles) {
  return !roles || roles.includes(role);
}

/**
 * Returns the help sections visible to `role`, with each section's topics
 * filtered to the ones that role may see. Sections that end up with no visible
 * topics (or that the role isn't allowed into) are dropped — so the drawer only
 * ever shows help for features the user can actually reach.
 */
export function getVisibleSections(role) {
  return helpSections
    .filter((section) => roleAllowed(role, section.roles))
    .map((section) => ({
      ...section,
      topics: section.topics.filter((topic) => roleAllowed(role, topic.roles)),
    }))
    .filter((section) => section.topics.length > 0);
}

/**
 * Resolves the section + topic to open for a given route and role, clamped to
 * what the role can see. Falls back to the first visible section/topic when the
 * route's default isn't visible to this role.
 */
export function resolveView(pathname, role) {
  const visible = getVisibleSections(role);
  if (visible.length === 0) return { sectionId: null, topicId: null };

  const wantedSection = getDefaultSection(pathname);
  const wantedTopic = getDefaultTopic(pathname);

  const section = visible.find((s) => s.id === wantedSection) || visible[0];
  const topic = section.topics.find((t) => t.id === wantedTopic) || section.topics[0];
  return { sectionId: section.id, topicId: topic.id };
}

// Map a route pathname to the most relevant help section.
export function getDefaultSection(pathname = '') {
  const p = pathname.toLowerCase();
  if (p.includes('/compose-sms') || p.includes('/group-sms') || p.includes('/bulk-messaging-upload') || p.includes('/message-templates')) return 'messaging';
  if (p.includes('/email') || p.includes('/push-notifications')) return 'channels';
  if (p.includes('/contact-management') || p.includes('/sender-id')) return 'contacts';
  if (p.includes('/credit') || p.includes('/my-credit')) return 'credits';
  if (p.includes('/api-configuration') || p.includes('/webhook')) return 'integrations';
  if (
    p.includes('/user-administration') ||
    p.includes('/statistical-reports') ||
    p.includes('/audit') ||
    p.includes('/sla-support-incidents') ||
    p.includes('/project-training') ||
    p.includes('/portal-content') ||
    p.includes('/data-export') ||
    p.includes('/messaging-analytics-dashboard')
  )
    return 'admin';
  return 'welcome';
}

// Map a route pathname to the most relevant topic within its section.
export function getDefaultTopic(pathname = '') {
  const p = pathname.toLowerCase();
  if (p.includes('/compose-sms')) return 'compose-sms';
  if (p.includes('/group-sms')) return 'group-sms';
  if (p.includes('/bulk-messaging-upload')) return 'bulk-upload';
  if (p.includes('/message-templates')) return 'templates';
  if (p.includes('/email')) return 'email';
  if (p.includes('/push-notifications')) return 'push';
  if (p.includes('/contact-management')) return 'contacts';
  if (p.includes('/sender-id')) return 'sender-ids';
  if (p.includes('/my-credit')) return 'my-credits';
  if (p.includes('/credit-allocation')) return 'allocation';
  if (p.includes('/api-configuration')) return 'api-keys';
  if (p.includes('/webhook')) return 'webhooks';
  if (p.includes('/user-administration')) return 'users';
  if (p.includes('/statistical-reports')) return 'reports';
  if (p.includes('/audit')) return 'audit';
  if (p.includes('/sla-support-incidents') || p.includes('/project-training')) return 'incidents-training';
  if (p.includes('/portal-content') || p.includes('/data-export')) return 'portal-export';
  if (p.includes('/messaging-analytics-dashboard')) return 'reports';
  if (p.includes('/my-profile')) return 'roles';
  return 'overview';
}
